import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import {
  AssignCardFeedCardsRequest,
  AssignCardFeedCardsResponse,
  CardFeedResponse,
  CreateCardFeedRequest,
  UnassignCardFeedCardResponse,
  UpdateCardFeedRequest,
} from 'src/queries/cardFeeds/cardFeeds.models'
import { cardFeedsQueryKeys } from 'src/queries/queries.constants'
import { PostPeopleInvitesRequest } from 'src/requests/postPeopleInvites.request'
import { BackendErrorPayload } from 'src/utils/error.utils'

import {
  patchCardFeed,
  postAssignCardFeedCards,
  postCreateCardFeed,
  postReinviteCardholder,
  postUnassignCardFeedCard,
} from '../network/cardFeeds.network'
import { OptionalMutationOptions } from './queries.utils'

export type CreateCardFeedVariables = {
  organizationId: string
  body: CreateCardFeedRequest
}

export type CreateCardFeedError = AxiosError<BackendErrorPayload<string>>

export const useCreateCardFeed = (
  options?: OptionalMutationOptions<
    CardFeedResponse,
    CreateCardFeedVariables,
    CreateCardFeedError
  >,
) =>
  useMutation({
    mutationFn: ({ organizationId, body }: CreateCardFeedVariables) =>
      postCreateCardFeed(organizationId, body),
    mutationKey: cardFeedsQueryKeys.create(),
    ...options,
  })

export type AssignCardFeedCardsVariables = {
  cardFeedId: string
  body: AssignCardFeedCardsRequest
}

export type AssignCardFeedCardsError = AxiosError<BackendErrorPayload<string>>

export const useAssignCardFeedCards = (
  options?: OptionalMutationOptions<
    AssignCardFeedCardsResponse,
    AssignCardFeedCardsVariables,
    AssignCardFeedCardsError
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}
  return useMutation({
    mutationFn: ({ cardFeedId, body }: AssignCardFeedCardsVariables) =>
      postAssignCardFeedCards(cardFeedId, body),
    mutationKey: cardFeedsQueryKeys.assign(),
    onSuccess: (data, variables, context) => {
      // Assignment flips card status to ASSIGNED; refetch the feed's cards so
      // the table reflects the change without a manual reload.
      void queryClient.invalidateQueries({
        queryKey: cardFeedsQueryKeys.cards(variables.cardFeedId),
      })
      onSuccess?.(data, variables, context)
    },
    ...restOptions,
  })
}

export type UpdateCardFeedVariables = {
  organizationId: string
  cardFeedId: string
  body: UpdateCardFeedRequest
}

export type UpdateCardFeedError = AxiosError<BackendErrorPayload<string>>

export const useUpdateCardFeed = (
  options?: OptionalMutationOptions<
    CardFeedResponse,
    UpdateCardFeedVariables,
    UpdateCardFeedError
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}
  return useMutation({
    mutationFn: ({
      organizationId,
      cardFeedId,
      body,
    }: UpdateCardFeedVariables) =>
      patchCardFeed(organizationId, cardFeedId, body),
    mutationKey: cardFeedsQueryKeys.update(),
    onSuccess: (data, variables, context) => {
      const detailKey = cardFeedsQueryKeys.detail(
        variables.organizationId,
        variables.cardFeedId,
      )
      // Reflect the renamed feed immediately so the header updates without a
      // visible refetch flicker, then invalidate to reconcile with the server.
      queryClient.setQueryData(detailKey, data)
      void queryClient.invalidateQueries({ queryKey: detailKey })
      onSuccess?.(data, variables, context)
    },
    ...restOptions,
  })
}

export type ReinviteCardholderVariables = {
  organizationId: string
  // Full (non-Partial) request: reinvite always sends emails + role +
  // cardManagerAccounts, unlike the looser redux invite thunk.
  body: PostPeopleInvitesRequest
}

export type ReinviteCardholderError = AxiosError<BackendErrorPayload<string>>

// Reinvite does not change card data, so no cache invalidation — the page
// surfaces success/failure via toast.
export const useReinviteCardholder = (
  options?: OptionalMutationOptions<
    void,
    ReinviteCardholderVariables,
    ReinviteCardholderError
  >,
) =>
  useMutation({
    mutationFn: ({ organizationId, body }: ReinviteCardholderVariables) =>
      postReinviteCardholder(organizationId, body),
    mutationKey: cardFeedsQueryKeys.reinvite(),
    ...options,
  })

export type UnassignCardFeedCardVariables = {
  cardFeedId: string
  creditCardId: string
}

export type UnassignCardFeedCardError = AxiosError<BackendErrorPayload<string>>

export const useUnassignCardFeedCard = (
  options?: OptionalMutationOptions<
    UnassignCardFeedCardResponse,
    UnassignCardFeedCardVariables,
    UnassignCardFeedCardError
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}
  return useMutation({
    mutationFn: ({ cardFeedId, creditCardId }: UnassignCardFeedCardVariables) =>
      postUnassignCardFeedCard(cardFeedId, creditCardId),
    mutationKey: cardFeedsQueryKeys.unassign(),
    onSuccess: (data, variables, context) => {
      // Unassigning flips the card back to UNASSIGNED; refetch the feed's cards
      // so the row updates without a manual reload.
      void queryClient.invalidateQueries({
        queryKey: cardFeedsQueryKeys.cards(variables.cardFeedId),
      })
      onSuccess?.(data, variables, context)
    },
    ...restOptions,
  })
}
